import type { FeatureCollection, MultiPolygon, Polygon, Point } from 'geojson'

import { getStaticJson } from '@xt/client/utils/get-static-json'
import { reportEntryLog } from '@xt/client/utils/log'
import { pointInMultiPolygon, pointInPolygon } from 'geojson-utils-ts'

export type UserLocationInfo = {
  province: string
  city?: string
  provinceCode: number
  cityCode?: number
}

export type Location = {
  longitude: number
  latitude: number
}

type ResultData = {
  name: string
  adcode: number
}

export type Location2positionResult = {
  province: ResultData
  city: null | ResultData
}

export const validAdcode = new Set([
  130000, 210000, 310000, 340000, 370000, 430000, 460000, 520000, 610000, 640000, 810000, 110000, 140000, 220000, 320000, 350000, 410000,
  440000, 500000, 530000, 620000, 650000, 820000, 120000, 150000, 230000, 330000, 360000, 420000, 450000, 510000, 540000, 630000, 710000
])

async function loadProvinceData() {
  const data = await getStaticJson<FeatureCollection<MultiPolygon | Polygon>>('/mocks/geojson/100000.json')

  if (!data) {
    reportEntryLog('system.geojson.error', {
      msg: 'fetch error',
      code: 100000
    })
  }

  return data ?? null
}

async function loadCityData(adcode: number) {
  if (!validAdcode.has(adcode)) {
    reportEntryLog('system.geojson.error', {
      msg: 'adcode invalid',
      code: adcode
    })
    return null
  }

  const data = await getStaticJson<FeatureCollection<MultiPolygon | Polygon>>(`/mocks/geojson/${adcode}.json`)

  if (!data) {
    reportEntryLog('system.geojson.error', {
      msg: 'fetch error',
      code: adcode
    })
  }

  return data ?? null
}

export async function location2position({ longitude, latitude }: Location): Promise<null | Location2positionResult> {
  const data = await loadProvinceData()

  if (!data) return null

  const point = {
    type: 'Point',
    coordinates: [longitude, latitude]
  } satisfies Point

  const prov = data.features.find(item =>
    item.geometry.type === 'MultiPolygon' ? pointInMultiPolygon(point, item.geometry) : pointInPolygon(point, item.geometry as Polygon)
  )

  console.warn('location2position debug0', prov)

  if (!prov || !prov.properties) {
    reportEntryLog('system.geojson.error', {
      msg: 'data properties miss',
      lng: longitude,
      lat: latitude
    })
    return null
  }

  const adcode = prov.properties.adcode as number

  const city = await loadCityData(adcode)

  if (!city) return null

  const result = city.features.find(item =>
    item.geometry.type === 'MultiPolygon' ? pointInMultiPolygon(point, item.geometry) : pointInPolygon(point, item.geometry as Polygon)
  )

  console.warn('location2position debug1', result)

  if (!result || !result.properties) {
    reportEntryLog('system.geojson.warn', {
      msg: 'data properties miss',
      lng: longitude,
      lat: latitude
    })
  }

  return {
    province: {
      name: prov.properties.name,
      adcode: prov.properties.adcode
    },
    city: result?.properties
      ? {
          name: result.properties.name,
          adcode: result.properties.adcode
        }
      : null
  }
}

export enum EnumGeolocationPositionError {
  /** 定位权限被拒绝 */
  PermissionDenied = 1,
  /** 地理位置不可用 */
  PositionUnavailable = 2,
  /** 超时 */
  TimeOut = 3
}

export async function checkLocationPermission(): Promise<boolean> {
  // 环境兼容校验
  if (!navigator || !navigator.permissions || !navigator.permissions.query) {
    reportEntryLog('system.geolocation.error', {
      msg: '当前浏览器不支持 Permissions API，无法读取定位权限状态'
    })
    return false
  }

  try {
    // 查询地理位置权限
    const status = await navigator.permissions.query({ name: 'geolocation' })

    // 权限状态枚举：
    // granted: 已永久允许
    // denied: 永久拒绝
    // prompt: 未授权，下次调用定位会弹窗询问
    switch (status.state) {
      case 'granted':
        return true
      case 'denied':
      case 'prompt':
        return false
      default:
        return false
    }
  } catch (err) {
    reportEntryLog('system.getlocation.error', {
      msg: '查询定位权限失败'
    })
    console.error('查询定位权限失败', err)
    return false
  }
}

export function getLocation(): Promise<null | Location> {
  return new Promise(resolve => {
    if (!navigator?.geolocation) {
      reportEntryLog('system.geolocation.error', {
        msg: '当前浏览器不支持定位API'
      })
      resolve(null)
      return
    }

    navigator.geolocation.getCurrentPosition(
      position => {
        const { latitude, longitude } = position.coords
        resolve({
          longitude,
          latitude
        })
      },
      (err: GeolocationPositionError) => {
        let msg = ''
        switch (err.code) {
          case EnumGeolocationPositionError.PermissionDenied:
            msg = '定位权限被拒绝，请允许定位；仅 localhost/HTTPS 域名支持定位'
            break
          case EnumGeolocationPositionError.PositionUnavailable:
            msg = '无法获取地理位置，GPS/网络信号丢失'
            break
          case EnumGeolocationPositionError.TimeOut:
            msg = '地理定位信息未能在规定时间内获得'
            break
          default:
            msg = `定位未知错误：${err.message}`
        }
        reportEntryLog('system.getlocation.error', {
          msg
        })
        resolve(null)
      }
    )
  })
}

export async function getUserLocation(): Promise<UserLocationInfo | null> {
  const res = await getLocation()

  if (!res) return null

  const local = await location2position(res)

  if (!local) return null

  const { province, city } = local

  return {
    province: province.name,
    city: city?.name,
    provinceCode: province.adcode,
    cityCode: city?.adcode
  }
}
